<script setup lang="ts">
import { parse } from 'qs';
import { faAngleUp } from '@fortawesome/pro-regular-svg-icons';
import {
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { type DayPrice, type Price } from './types';
import DayPriceView from './DayPriceView.vue';
import { ReserveType } from '@utils/itinerary';
import { type TripSearchForm } from '@search/trip/TripSearchForm';

defineProps<{
    compactMode?: boolean;
}>();
const itineraryStore = useItineraryStore();
const { isSelectingReturn } = storeToRefs(itineraryStore);
const searchFormStore = useSearchFormStore();
const { reserveType } = storeToRefs(searchFormStore);
const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
const { handleDateChange } = itineraryStore;
const currentModule = useCurrentItineraryModule();
const { priceLoader, isDomestic, prices, viewUrl, departureDate } =
    moduleToRefs(currentSearchForm.value);
const wrapper = ref<HTMLDivElement>();
const scrollContainer = ref<HTMLDivElement>();
const dayPriceView = ref<InstanceType<typeof DayPriceView>[]>([]);
const gtm = useGtm();
const router = useRouter();
const route = useRoute();
const { isMobile } = useSize();
const isDragging = ref(false);
const isSmooth = ref(false);
const pos = ref({ left: 0, x: 0 });
const collapsed = ref(false);
const havePriceChart = computed(
    () => !!isDomestic.value && reserveType.value !== ReserveType.Taxi
);
const currentDate = computed(
    () =>
        currentModule.value.currentPackageSet?.date ||
        departureDate.value ||
        new Date()
);
const daysList = computed(
    () => prices.value?.map(p => dayConfig.gregory(p.date).toDate()) || []
);
const absoluteMaxPrice = computed(
    () => priceLoader.value?.maxPrice || Number.MIN_VALUE
);
const absoluteMinPrice = computed(
    () => priceLoader.value?.minPrice || Number.MAX_VALUE
);
const daysPriceList = computed<DayPrice[]>(() => {
    if (prices.value?.length) {
        const currentDayjs = dayConfig.gregory(currentDate.value);
        return prices.value.map(price => {
            const date = dayConfig.gregory(price.date).toDate();
            return {
                key: price.date,
                date,
                price: price.value,
                isMinPrice: price.priceStatus === 'min',
                isMaxPrice: price.priceStatus === 'max',
                isSelected: currentDayjs.isSame(date, 'day'),
                isAvailable: !!price.value && price.value !== '-',
                isLoading: price.isLoading,
                disabled:
                    isSelectingReturn.value &&
                    (dayConfig.gregory(date).isBefore(departureDate.value) ||
                        dayConfig
                            .gregory(date)
                            .isSame(departureDate.value, 'day')),
                chartRatio: getRatio(price.value),
            };
        });
    } else {
        return Array(8)
            .fill(null)
            .map(
                (_, i) =>
                    ({
                        key: `${i}`,
                        isLoading: true,
                        date: new Date(),
                        price: '-',
                        isMinPrice: false,
                        isMaxPrice: false,
                        isSelected: false,
                        isAvailable: false,
                        disabled: false,
                        chartRatio: 0,
                    }) as DayPrice
            ) as DayPrice[];
    }
});
const indexOfCurrentValue = computed(() =>
    daysList.value.findIndex(d =>
        dayConfig.gregory(d).isSame(currentDate.value, 'day')
    )
);

const getRatio = (price: Price) => {
    if (
        !absoluteMinPrice.value ||
        !absoluteMaxPrice.value ||
        !price ||
        price === '-'
    ) {
        return 0;
    }
    const min = absoluteMinPrice.value * 0.2;
    const nPrice = price - min;
    const max = absoluteMaxPrice.value - min;
    const mapMax = absoluteMaxPrice.value >= 5 ? 90 : 100;
    return (nPrice * mapMax) / max;
};
const toggle = () => {
    collapsed.value = !collapsed.value;
};
const initDrag = () =>
    dayPriceView.value?.[indexOfCurrentValue.value]?.$el?.addEventListener(
        'mousedown',
        mouseDownHandler
    );
const removeDrag = () =>
    dayPriceView.value?.[indexOfCurrentValue.value]?.$el?.removeEventListener(
        'mousedown',
        mouseDownHandler
    );
const mouseDownHandler = (e: MouseEvent) => {
    isDragging.value = true;
    pos.value = {
        left: scrollContainer.value?.scrollLeft ?? 0,
        x: e.clientX,
    };
    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
};
const mouseMoveHandler = (e: MouseEvent) => {
    const dx = e.clientX - pos.value.x;
    scrollContainer.value!.scrollLeft = pos.value.left - dx;
};
const mouseUpHandler = () => {
    isDragging.value = false;
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);
};
const scrollTo = (value: Date) => {
    const dayIndex = daysList.value.findIndex(d =>
        dayConfig.gregory(d).isSame(value, 'day')
    );
    const child = scrollContainer.value?.children[dayIndex] as
        | HTMLElement
        | undefined;
    if (dayIndex < 0 || !child) {
        return;
    }
    scrollContainer.value?.scrollTo({
        left:
            child?.offsetLeft -
            scrollContainer.value?.offsetWidth / 2 -
            scrollContainer.value?.offsetLeft +
            child.clientWidth / 2,
        behavior: 'auto',
    });
};
const goNext = () =>
    scrollContainer.value?.scrollTo({
        left: scrollContainer.value?.scrollLeft - 100,
        behavior: 'auto',
    });
const goPrev = () =>
    scrollContainer.value?.scrollTo({
        left: scrollContainer.value?.scrollLeft + 100,
        behavior: 'auto',
    });
const goYesterday = () =>
    dateChange(daysList.value[indexOfCurrentValue.value - 1]);
const goTomorrow = () =>
    dateChange(daysList.value[indexOfCurrentValue.value + 1]);
const dateChange = (date: Date) => {
    handleDateChange(date);
    const [url, query] = viewUrl.value.split('?');
    const queries = parse(query) as any;
    router.replace({
        path: url,
        query: { ...route.query, ...queries },
    });
};

const onPriceClick = (price: DayPrice) => {
    gtm?.trackEvent({
        event: 'search-list-price',
        reserveType: reserveType.value,
        preDate: currentDate.value.toISOString(),
        newDate: price.date.toISOString(),
        isMin: price.isMinPrice,
        isMax: price.isMaxPrice,
    });
    dateChange(price.date);
};

watch(daysPriceList, () => initDrag());
watch(currentDate, (newVal, oldVal) => {
    removeDrag();
    if (!dayConfig.gregory(newVal).isSame(oldVal, 'day')) {
        scrollTo(currentDate.value);
    }
});
watch(prices, () => nextTick(() => scrollTo(currentDate.value)), {
    deep: true,
});
onMounted(() => {
    nextTick(() => {
        removeDrag();
        scrollTo(currentDate.value);
        setTimeout(() => {
            isSmooth.value = false;
        }, 200);
    });
    if (!isMobile.value) {
        initDrag();
    }
});
defineExpose({
    goYesterday,
    goTomorrow,
});
</script>

<template>
    <div
        ref="wrapper"
        class="price-calendar-wrapper"
        :class="{
            'without-header': compactMode || !havePriceChart,
        }"
    >
        <header v-if="!compactMode" class="calendar-header">
            <div class="title" @click="toggle">
                <mr-icon
                    :icon="faAngleUp"
                    class="arrow-icon"
                    size="x-small"
                    :class="{ collapsed }"
                />
                به‌صرفه‌ترین قیمت در هر روز
            </div>
        </header>
        <div
            v-show="!collapsed"
            class="price-calendar-container"
            :class="{ dragging: isDragging }"
        >
            <mr-button
                id="desktop-prev-day"
                variant="transparent"
                :icon-button="{
                    icon: faChevronRight,
                    color: 'var(--blue-400)',
                }"
                :disabled="indexOfCurrentValue === 0"
                class="nav-button"
                @click="goPrev"
            />
            <div
                ref="scrollContainer"
                class="day-price-list"
                :class="{ smooth: isSmooth && !isDragging }"
            >
                <template v-for="dayPrice in daysPriceList" :key="dayPrice.key">
                    <DayPriceView
                        ref="dayPriceView"
                        class="day-price-view-drag"
                        :item="dayPrice"
                        :all-is-loading="!prices?.length"
                        @click="onPriceClick(dayPrice)"
                    />
                </template>
            </div>
            <mr-button
                id="desktop-next-day"
                variant="transparent"
                :icon-button="{
                    icon: faChevronLeft,
                    color: 'var(--blue-400)',
                }"
                class="nav-button"
                @click="goNext"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
.price-calendar-wrapper {
    @include shadow();
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 8px 0 8px;
    background: white;
    border-radius: 12px;
    margin-top: 8px;
    overflow: hidden;
    @include mobile-only {
        padding: 0;
        &.without-header {
            padding-top: 8px;
        }
    }
    &.compact-mode {
        flex-direction: row;
        align-items: center;
        padding-top: 8px;
        .price-calendar-container {
            max-width: calc(100% - 72px);
        }
        @include mobile-only {
            position: relative;
            padding-top: 12px;
            .price-calendar-container {
                max-width: 100%;
            }
        }
    }
}

.calendar-header {
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
    padding: 0 12px;
    @include mobile-only {
        padding: 4px 16px 4px 8px;
        margin-bottom: 0;
    }
    .title {
        @include tg-m($w5);
        display: flex;
        align-items: center;
        .arrow-icon {
            display: none;
            @include mobile-only {
                flex-shrink: 0;
                display: flex;
                margin-left: 8px;
                transition: all 200ms;
                &.collapsed {
                    transform: rotate(180deg);
                }
            }
        }
    }
}

.price-calendar-container {
    display: flex;
    align-items: center;
    max-width: 100%;
    .nav-button {
        flex-shrink: 0;
        margin-bottom: 8px;
        @include mobile-only {
            display: none;
        }
    }
    &.dragging {
        &,
        .day-price-view-drag {
            cursor: grabbing;
            user-select: none;
        }
    }
}

.day-price-list {
    @include no-scrollbar;
    display: flex;
    align-items: center;
    overflow-y: hidden;
    overflow-x: scroll;
    padding-bottom: 8px;
    @include mobile-only {
        padding-bottom: 12px;
    }

    &.smooth {
        scroll-behavior: smooth;
    }
}

.compare-price-button {
    all: unset;
    @include tg-xs($w7, usePalette());
    margin-right: 12px;
    @include mobile-only {
        display: none;
    }
}

.compare-price-mobile-button {
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
}
</style>
